import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EmailIntelligenceManager } from '../components/email/EmailIntelligenceManager';
import { MealHistoryReport } from '../components/dashboard/MealHistoryReport';
import { WeeklyReportGenerator } from '../components/reports/WeeklyReportGenerator';
import { apiClient } from '../config/api';

interface ReportsPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
}

export const ReportsPage: React.FC<ReportsPageProps> = ({ user, coachMode, clientId }) => {
  const [activeView, setActiveView] = useState<'overview' | 'email-manager' | 'meal-history' | 'weekly-report'>('overview');

  // Fetch meal portfolio data for meal history report
  const { data: portfolioData, isLoading: portfolioLoading } = useQuery({
    queryKey: coachMode ? ['client-meal-portfolio', clientId] : ['meal-portfolio', user?.id],
    queryFn: () => coachMode && clientId
      ? apiClient.getClientMeals(clientId, '90d')
      : apiClient.getMealPortfolio(90),
    enabled: (coachMode ? !!clientId : !!user?.id) && activeView === 'meal-history',
  });

  if (activeView === 'email-manager') {
    return <EmailIntelligenceManager user={user} />;
  }

  if (activeView === 'meal-history') {
    return (
      <div className="space-y-6">
        {/* Back button */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setActiveView('overview')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Reports
          </button>
        </div>

        {portfolioLoading ? (
          <div className="bg-gray-200 rounded-lg h-96 animate-pulse" />
        ) : (
          <MealHistoryReport mealData={portfolioData} />
        )}
      </div>
    );
  }

  if (activeView === 'weekly-report') {
    return (
      <WeeklyReportGenerator
        user={user}
        onBack={() => setActiveView('overview')}
        coachMode={coachMode}
        clientId={clientId}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-sm">
        <div className="px-6 py-8 sm:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-white">
                Reports & Email Templates 📧
              </h1>
              <p className="mt-2 text-orange-100">
                Automated insights and email generation for {user?.fullName || user?.email}
              </p>
            </div>
            <button 
              onClick={() => setActiveView('email-manager')}
              className="bg-white text-orange-600 px-4 py-2 rounded-md font-medium hover:bg-orange-50 transition-colors"
            >
              🚀 Launch Email Intelligence
            </button>
          </div>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          className="card cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setActiveView('meal-history')}
        >
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Complete Meal History</h3>
          </div>
          <div className="card-body">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                <span className="text-2xl">🍽️</span>
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">All Meals & Photos</h4>
              <p className="text-xs text-gray-500 mb-3">
                Detailed view of all logged meals with photos, foods, and nutrition data.
              </p>
              <div className="text-xs font-medium text-green-600">✅ Available Now!</div>
            </div>
          </div>
        </div>

        <div 
          className="card cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setActiveView('email-manager')}
        >
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Weekly Email Generator</h3>
          </div>
          <div className="card-body">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
                <span className="text-2xl">✉️</span>
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Automated Client Emails</h4>
              <p className="text-xs text-gray-500 mb-3">
                Generate personalized weekly nutrition insights and recommendations.
              </p>
              <div className="text-xs font-medium text-green-600">✅ Available Now!</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Insight Templates</h3>
          </div>
          <div className="card-body">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                <span className="text-2xl">💡</span>
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Smart Recommendations</h4>
              <p className="text-xs text-gray-500 mb-3">
                AI-powered insights based on nutrient analysis and patterns.
              </p>
              <div className="text-xs text-gray-400">Phase 2B Priority</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Red Flag Alerts</h3>
          </div>
          <div className="card-body">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-3">
                <span className="text-2xl">🚨</span>
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Concern Detection</h4>
              <p className="text-xs text-gray-500 mb-3">
                Automatic alerts for severe deficiencies or concerning patterns.
              </p>
              <div className="text-xs text-gray-400">Phase 2B Priority</div>
            </div>
          </div>
        </div>

        <div
          className="card cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setActiveView('weekly-report')}
        >
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">AI Weekly Reports</h3>
          </div>
          <div className="card-body">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-3">
                <span className="text-2xl">📈</span>
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Chatbot-Powered Analysis</h4>
              <p className="text-xs text-gray-500 mb-3">
                AI generates personalized weekly insights using your real meal data. No hallucinations!
              </p>
              <div className="text-xs font-medium text-green-600">✅ Available Now!</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Food Recommendations</h3>
          </div>
          <div className="card-body">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full mb-3">
                <span className="text-2xl">🍎</span>
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Targeted Suggestions</h4>
              <p className="text-xs text-gray-500 mb-3">
                Specific foods to address identified nutritional gaps.
              </p>
              <div className="text-xs text-gray-400">Phase 2B Priority</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Report Scheduler</h3>
          </div>
          <div className="card-body">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-100 rounded-full mb-3">
                <span className="text-2xl">📅</span>
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Automated Delivery</h4>
              <p className="text-xs text-gray-500 mb-3">
                Schedule weekly emails and reports for all clients.
              </p>
              <div className="text-xs text-gray-400">Phase 2B</div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Email Intelligence Preview</h3>
        </div>
        <div className="card-body">
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="font-medium text-gray-900 mb-3">Sample Weekly Email Content:</h4>
            <div className="space-y-3 text-sm text-gray-700">
              <p><strong>This Week's Wins:</strong> Your B12 levels improved by 40% - great job adding more Marmite!</p>
              <p><strong>Areas for Focus:</strong> Vitamin D is still low (5% of RDA). Consider adding fortified foods or supplements.</p>
              <p><strong>Specific Recommendations:</strong> Add 2-3 servings of fatty fish this week for omega-3 and vitamin D.</p>
              <p><strong>Red Flag Alert:</strong> Iron levels dropped to 30% of RDA - let's discuss iron-rich meal options.</p>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              ✨ This content will be generated automatically from your client's nutrition data
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};